import { IRemake } from 'lib/types/clinic/projectTypes';
import { IPatient } from 'lib/types/patientTypes';
import { useDidUpdateEffect } from 'lib/utils';
import { debounce } from 'lodash';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmployeeActions, PatientActions, ProjectActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { useCreateProjectInfoStore } from 'store/local/projectCreateinfo';
import { useRecording } from '../useRecording';
import { Tooth } from 'lib/types/projectTypes';
import { useHistory, useParams } from 'react-router-dom';
import { makeCaseIdFnClinic } from 'lib/library';

interface FormValues {
  projectName: string;
  patientCodeIdx: number | null;
  dueDate: Moment;
  manager: number | null;
  remakeType: IRemake[] | null;
  senderMemo: string | null;
}

export const useCreateProjectInfo = (from: 'create' | 'detail' | 'patient') => {
  // SECTION: declare server state
  const {
    user,
    projectInit,
    projectInitSuccess,
    projectData,
    copyProjectData,
    projectPatientsData: { list: projectPatientsList, pagingData: patientsPagingData },
    projectPatientsSuccess,
    projectPatientsPending,
    editProjectSuccess,
    createProjectData,
    createProjectSuccess,
    patientData,
    newPatient: { patientInfo },
    newPatientSuccess,
    remakeTypeList,
    remakeTypeListSuccess,
    managerListData,
    managerListSuccess,
    loadedSource,
  } = useShallowAppSelector(state => ({
    user: state.user.user || {},
    projectInit: state.project.initProject.data,
    projectInitSuccess: state.project.initProject.success,
    projectData: state.project.project.data,
    copyProjectData: state.project.copyProject.data,
    projectPatientsData: state.patient.projectPatients.data || {},
    projectPatientsSuccess: state.patient.projectPatients.success,
    projectPatientsPending: state.patient.projectPatients.pending,
    editProjectSuccess: state.project.editProject.success,
    createProjectData: state.project.createProject.data,
    createProjectSuccess: state.project.createProject.success,
    patientData: state.patient.patient.data,
    newPatient: state.patient.newPatient.data || { patientInfo: {}, msg: {} },
    newPatientSuccess: state.patient.newPatient.success,
    remakeTypeList: state.user.fetchMyRemakeList.data?.list,
    remakeTypeListSuccess: state.user.fetchMyRemakeList.success,
    managerListData: state.employee.managerList.data,
    managerListSuccess: state.employee.managerList.success,
    loadedSource: state.app.loadedSource,
  }));

  // SECTION: declare local state
  const {
    pageType,
    isValidInfo,
    setIsValidInfo,
    patientName,
    setPatientName,
    setIsInfoOpen,
    projectName,
    setProjectName,
    patientCodeIdx,
    setPatientCodeIdx,
    dueDate,
    setDueDate,
    manager,
    setManager,
    reset: createProjectInfoReset,
    setMultiple: setCreateProjectInfoMultiple,
  } = useCreateProjectInfoStore();
  const {
    isRecording,
    isTranscriptionLoading,
    recordedText,
    recordingTime,
    onStartRecording,
    onStopRecording,
  } = useRecording();

  // SECTION: declare variable
  const userId = loadedSource === 'server' ? user.userGroupIdx : user.groupIdx;
  const groupCode = user.groupCode;
  const { t } = useTranslation();
  const { pcode: projectCode } = useParams() as { pcode: string };
  const history = useHistory();
  const isEdit = from === 'detail';
  const isPatient = from === 'patient';
  const { handleSubmit, control, watch, setValue, reset } = useForm<FormValues>({
    defaultValues: {
      projectName: '',
      patientCodeIdx: null,
      dueDate: moment(),
      manager: null,
    },
  });
  const [patientOptionList, setPatientOptionList] = useState<IPatient[]>([]);
  const [managerOptionList, setManagerOptionList] = useState<any[]>([]);
  const [remakeOptionList, setRemakeOptionList] = useState<any[]>([]);
  const hasMoreProjectPatients = patientsPagingData?.page < patientsPagingData?.totalPage;
  const [isOpenPatientModal, setIsOpenPatientModal] = useState(false);
  const [isOpenMicrophoneModal, setIsOpenMicrophoneModal] = useState(false);
  const [isOpenDueDate, setIsOpenDueDate] = useState(false);
  const [isSyncPatientName, setIsSyncPatientName] = useState(true);
  const [isCreateProject, setIsCreateProject] = useState(false);
  const justSelectedPatientRef = useRef(false);
  const justSelectedTimerRef = useRef<any>(null);
  const projectInfo = useMemo(() => {
    if (!projectData && !copyProjectData) return;

    if (projectData) {
      return projectData?.projectInfo;
    }
    if (copyProjectData) {
      return copyProjectData?.projectInfo;
    }
  }, [projectData, copyProjectData]);
  const projectCount = useMemo(() => {
    return !!projectInit && projectInit.projectCount;
  }, [projectInit]);
  const isNewPatient = useMemo(() => {
    return !watch('patientCodeIdx') && !!patientName && patientName !== '';
  }, [watch('patientCodeIdx'), patientName]);
  const caseId = useMemo(() => {
    return makeCaseIdFnClinic({
      newDate: null,
      patientName: patientName?.replace(/ /g, ''),
      groupCode: groupCode,

      caseCount: projectCount,
      caseIdValue: watch('projectName'),
    })(1);
  }, [patientName, groupCode, projectCount, watch('projectName')]);
  const projectId = useMemo(() => {
    if (pageType === 'edit' || pageType === 'copy') {
      if (!projectInfo) return;

      return projectInfo?.projectId;
    } else {
      return caseId;
    }
  }, [caseId, projectInfo]);

  // SECTION: declare dependency
  useEffect(() => {
    if (loadedSource === 'server') {
      EmployeeActions.fetch_manager_list_request();
    }
    if (loadedSource === 'local') {
      EmployeeActions.fetch_manager_list_request({
        channel: 'getManagerList',
        data: {},
      });
    }
  }, []);

  useEffect(() => {
    if (!projectInfo) return;

    const patientInfo = projectInfo?.patientInfo;
    const newPatientName = `${patientInfo?.lastName || ''}${patientInfo?.firstName || ''}`;

    setProjectName(projectInfo?.projectName);
    setPatientCodeIdx(patientInfo?.patientCodeIdx);
    setPatientName(newPatientName);
    setDueDate(projectInfo?.dueDate);
    setManager(projectInfo?.managerIdx);

    reset({
      projectName: projectInfo?.projectName,
      patientCodeIdx: patientInfo?.patientCodeIdx,
      dueDate: !!projectInfo?.dueDate ? moment.unix(projectInfo?.dueDate) : moment(),
      manager: projectInfo?.managerIdx,
    });
  }, [projectInfo]);

  useDidUpdateEffect(() => {
    if (projectPatientsSuccess && !!projectPatientsList) {
      setPatientOptionList(draft => [...draft, ...projectPatientsList]);
    }
  }, [projectPatientsSuccess]);

  useDidUpdateEffect(() => {
    if (newPatientSuccess && !!patientInfo) {
      const newPatientName = `${patientInfo?.lastName || ''}${patientInfo?.firstName || ''}`;

      setPatientName(newPatientName);
      setPatientCodeIdx(patientInfo.patientCodeIdx);
      setValue('patientCodeIdx', patientInfo.patientCodeIdx);
      setPatientOptionList([]);
      if (loadedSource === 'server') {
        PatientActions.fetch_project_patients_request({ keyword: newPatientName, page: 1 });
      }
      if (loadedSource === 'local') {
        PatientActions.fetch_project_patients_request({
          channel: 'getPatientList',
          data: { keyword: newPatientName, page: 1 },
        });
      }
    }
  }, [newPatientSuccess]);

  useDidUpdateEffect(() => {
    if (newPatientSuccess && patientCodeIdx && isCreateProject) {
      const data = {
        dueDate: dueDate,
        patientCodeIdx: patientCodeIdx,
        projectName: projectName,
        manager: manager,
      };

      handleCreateProject(data);
    }
  }, [newPatientSuccess, patientCodeIdx, isCreateProject]);

  useEffect(() => {
    if (!!managerListData?.list) {
      const fetchManagerList = managerListData?.list?.map((employee: any) => {
        return { value: employee.employeeIdx, label: employee.userName };
      });

      setManagerOptionList(fetchManagerList);
    }
  }, [managerListSuccess]);

  useEffect(() => {
    if (!!remakeTypeList) {
      const fetchRemakeList = remakeTypeList?.map((remake: IRemake) => {
        return { value: remake.remakeTypeIdx, label: remake.remakeTypeName };
      });

      setRemakeOptionList(fetchRemakeList);
    }
  }, [remakeTypeListSuccess, remakeTypeList]);

  useEffect(() => {
    setIsValidInfo(!!watch('projectName') && !!patientName);
  }, [watch('projectName'), patientName]);

  useDidUpdateEffect(() => {
    setValue('senderMemo', recordedText);
  }, [recordedText]);

  useEffect(() => {
    if (loadedSource === 'server') {
      PatientActions.fetch_project_patients_request({ keyword: '', page: 1 });
    }
    if (loadedSource === 'local') {
      PatientActions.fetch_project_patients_request({
        channel: 'getPatientList',
        data: { keyword: '', page: 1 },
      });
    }
  }, []);

  useEffect(() => {
    if (isPatient) {
      const newProjectName = t('PROJECT_PATIENT_NAME', { patientName: patientName });

      setProjectName(newProjectName);
      setValue('projectName', newProjectName);
    }
  }, [isPatient, patientName]);

  useEffect(() => {
    if (!isSyncPatientName) return;

    if (patientName) {
      const newProjectName = t('PROJECT_PATIENT_NAME', { patientName: patientName });

      setProjectName(newProjectName);
      setValue('projectName', newProjectName);
    } else {
      setProjectName('');
      setValue('projectName', '');
    }
  }, [patientName, isSyncPatientName]);

  useEffect(() => {
    if (editProjectSuccess) {
      setIsInfoOpen(false);
      setCreateProjectInfoMultiple({
        dueDate: null,
        manager: null,
        patientCodeIdx: null,
        projectName: '',
        patientName: '',
      });
      if (loadedSource === 'local') {
        ProjectActions.fetch_project_request({
          channel: 'getProjectDetail',
          data: { projectCode: projectCode },
        });
      }
    }
  }, [editProjectSuccess]);

  useEffect(() => {
    if (createProjectSuccess && !!createProjectData) {
      const projectCode = createProjectData?.projectInformation?.projectCode;

      setIsInfoOpen(false);
      createProjectInfoReset();

      history.push(`/project/detail/${projectCode}`);
    }
  }, [createProjectSuccess, createProjectData]);

  useEffect(() => {
    if (!!patientData) {
      const patientInfo = patientData?.patientInfo;

      const newPatientName = `${patientInfo?.lastName || ''}${patientInfo?.firstName || ''}`;

      setPatientName(newPatientName);
      setPatientCodeIdx(patientInfo?.patientCodeIdx);
      setValue('patientCodeIdx', patientInfo?.patientCodeIdx);
    }
  }, [patientData]);

  // SECTION: declare function
  const handleOptionClick = (value: string | number, label: string) => {
    setPatientName(label);
    setIsSyncPatientName(true);
    justSelectedPatientRef.current = true;

    // 이전 타이머가 있으면 클리어
    if (justSelectedTimerRef.current) {
      clearTimeout(justSelectedTimerRef.current);
    }

    // 선택 후 플래그 리셋
    justSelectedTimerRef.current = setTimeout(() => {
      justSelectedPatientRef.current = false;
    }, 700);
  };

  const handlePatientSearch = debounce((value: string) => {
    // 방금 환자를 선택했으면 아무것도 하지 않음
    if (justSelectedPatientRef.current) {
      return;
    }

    if (value === '') {
      setIsSyncPatientName(true);
    }

    const currentPatientCodeIdx = watch('patientCodeIdx');

    // API 호출
    if (loadedSource === 'server') {
      PatientActions.fetch_project_patients_request({ keyword: value || '', page: 1 });
    }
    if (loadedSource === 'local') {
      PatientActions.fetch_project_patients_request({
        channel: 'getPatientList',
        data: { keyword: value || '', page: 1 },
      });
    }

    setPatientOptionList([]);

    // 다음 경우에만 patientName 업데이트:
    // 1. 선택된 환자가 없을 때
    // 2. 선택된 환자가 있는데 검색어가 현재 환자명과 다를 때
    if (!currentPatientCodeIdx) {
      setPatientName(value);
    } else if (value !== patientName) {
      setPatientName(value);
      setValue('patientCodeIdx', null);
      setPatientCodeIdx(null);
    }
    // value === patientName인 경우: 아무것도 하지 않음 (선택 직후 상황)
  }, 500);

  const handlePatientFetch = debounce((first: boolean) => {
    if (first) {
      if (loadedSource === 'server') {
        return PatientActions.fetch_project_patients_request({ keyword: '', page: 1 });
      }
      if (loadedSource === 'local') {
        return PatientActions.fetch_project_patients_request({
          channel: 'getPatientList',
          data: { keyword: '', page: 1 },
        });
      }
    }

    const { page, totalPage } = patientsPagingData;

    if (page >= totalPage) return;

    if (loadedSource === 'server') {
      PatientActions.fetch_project_patients_request({
        keyword: patientName || '',
        page: page + 1,
      });
    }

    if (loadedSource === 'local') {
      PatientActions.fetch_project_patients_request({
        channel: 'getPatientList',
        data: {
          keyword: patientName || '',
          page: page + 1,
        },
      });
    }
  }, 500);

  const handleCreateInfoClose = () => {
    setIsInfoOpen(false);
  };

  const handleCreateProject = (data: any) => {
    const submitData = {
      projectId: projectId,
      exocadVersion: 3,
      dueDate: data?.dueDate?.unix(),
      patientCodeIdx: data?.patientCodeIdx,
      projectName: data?.projectName,
      participant: {
        client: userId,
        manager: data?.manager,
      },
    };

    if (loadedSource === 'server') {
      ProjectActions.create_project_request({
        ...submitData,
      });
    }
    if (loadedSource === 'local') {
      ProjectActions.create_project_request({
        channel: 'createProject',
        data: { ...submitData },
      });
    }
  };

  const handleCreateInfoSave = (data: any) => {
    // 모든 input 요소를 가져와서 환자명과 프로젝트명 input 찾기
    const inputs = document.querySelectorAll<HTMLInputElement>('input[type="text"]');
    let patientInput: HTMLInputElement | undefined;
    let projectNameInput: HTMLInputElement | undefined;

    inputs.forEach((input) => {
      if (input.placeholder?.includes('환자') || input.placeholder?.includes('Patient')) {
        patientInput = input;
      }
      if (input.placeholder?.includes('프로젝트') || input.placeholder?.includes('Project') ||
          input.placeholder?.includes('PROJECT')) {
        projectNameInput = input;
      }
    });

    // 현재 포커스된 요소가 input이면 blur 처리
    const activeElement = document.activeElement as HTMLInputElement;
    if (activeElement && activeElement.tagName === 'INPUT') {
      activeElement.blur();
    }

    // 각 input의 최종 값 가져오기
    let finalPatientName = patientName;
    let finalProjectName = projectName;
    let needsUpdate = false;

    if (patientInput) {
      const currentPatientValue = patientInput.value;
      if (currentPatientValue !== patientName) {
        finalPatientName = currentPatientValue;
        setPatientName(currentPatientValue);
        needsUpdate = true;
      }
    }

    if (projectNameInput) {
      const currentProjectValue = projectNameInput.value;
      if (currentProjectValue !== projectName) {
        finalProjectName = currentProjectValue;
        setProjectName(currentProjectValue);
        setValue('projectName', currentProjectValue);  // react-hook-form 값도 업데이트
        needsUpdate = true;
      }
    }

    // 값이 업데이트되었으면 setTimeout 사용, 아니면 바로 실행
    const executeCreateProject = () => {
      setIsCreateProject(true);

      if (!!data?.patientCodeIdx) {
        handleCreateProject({
          ...data,
          projectName: finalProjectName  // 업데이트된 프로젝트명 사용
        });
      } else {
        setDueDate(data?.dueDate);
        setPatientCodeIdx(data?.patientCodeIdx);
        setManager(data?.manager);

        const patient = {
          chartNumber: moment().format('YYYYMMDDHHmmss'),
          firstName: finalPatientName,  // 업데이트된 환자명 사용
          lastName: '',
          birthday: '',
          gender: 'N',
          comment: '',
          identification: '',
          pms: '',
        };
        if (loadedSource === 'server') {
          PatientActions.new_patient_request(patient);
        }
        if (loadedSource === 'local') {
          PatientActions.new_patient_request({
            channel: 'createPatient',
            data: { ...patient },
          });
        }
      }
    };

    if (needsUpdate) {
      setTimeout(executeCreateProject, 50);
    } else {
      executeCreateProject();
    }
  };

  const handleDetailInfoSave = (data: any) => {
    const submitData = {
      dueDate: data?.dueDate?.unix(),
      patientCodeIdx: data?.patientCodeIdx,
      projectName: data?.projectName,
      participant: {
        manager: data?.manager,
      },
    };

    if (loadedSource === 'server') {
      ProjectActions.edit_project_request({
        projectCode,
        data: {
          ...submitData,
        },
      });
    }
    if (loadedSource === 'local') {
      ProjectActions.edit_project_request({
        channel: 'updateProject',
        data: { projectCode, ...submitData },
      });
    }
  };

  const handleCreatePatient = () => {
    setIsOpenPatientModal(true);
  };

  const handleClosePatientModal = () => {
    setIsOpenPatientModal(false);
  };

  const handleCloseMicrophoneModal = () => {
    setIsOpenMicrophoneModal(false);
  };

  const handleClickDueDate = () => {
    setIsOpenDueDate(!isOpenDueDate);
  };

  const handleOpenChangeDueDate = (open: boolean) => {
    setIsOpenDueDate(open);
  };

  const handleMicrophoneClick = (type: 'start' | 'stop') => {
    if (type === 'start') {
      onStartRecording();
    }
    if (type == 'stop') {
      onStopRecording();
    }
  };

  const handleConfirmMicrophoneModal = () => {
    onStartRecording();
    setIsOpenMicrophoneModal(false);
  };

  const handleProjectNameChange = (_: string) => {
    setIsSyncPatientName(false);
  };

  return {
    control,
    watch,
    isEdit,
    isPatient,
    patientOptionList: patientOptionList,
    managerOptionList: managerOptionList,
    remakeOptionList: remakeOptionList,
    patientName,
    hasMoreProjectPatients: hasMoreProjectPatients,
    projectPatientsPending: projectPatientsPending,
    isValidInfo,
    isOpenPatientModal,
    isOpenMicrophoneModal,
    isOpenDueDate,
    isRecording,
    isTranscriptionLoading,
    isNewPatient,
    recordingTime,
    loadedSource,
    onOptionClick: handleOptionClick,
    onPatientSearch: handlePatientSearch,
    onPatientFetch: handlePatientFetch,
    onCreateInfoClose: handleCreateInfoClose,
    onCreateInfoSave: handleSubmit(handleCreateInfoSave),
    onDetailInfoSave: handleSubmit(handleDetailInfoSave),
    onCreatePatient: handleCreatePatient,
    onClosePatientModal: handleClosePatientModal,
    onCloseMicrophoneModal: handleCloseMicrophoneModal,
    onClickDueDate: handleClickDueDate,
    onOpenChangeDueDate: handleOpenChangeDueDate,
    onMicrophoneClick: handleMicrophoneClick,
    onConfirmMicrophoneModal: handleConfirmMicrophoneModal,
    onProjectNameChange: handleProjectNameChange,
  };
};
